<template>
  <v-card
    outlined
    :class="{
      'subscription-card': true,
      'current-plan': isCurrentPlan,
      'popular-plan': isHighlighted
    }"
    height="100%"
  >
    <div v-if="recommended" class="recommended-band">{{ recommendedLabel }}</div>
    <div v-else-if="isProPlan" class="popular-badge">Popular</div>
    <v-card-title class="justify-center">
      {{ planName }}
    </v-card-title>
    <div v-if="displayTagline" class="plan-tagline">{{ displayTagline }}</div>
    <v-card-text class="text-center">
      <!-- The member's own plan is named here and kept neutral, so only the
           recommended card carries the accent. -->
      <v-chip
        v-if="isCurrentPlan"
        small
        outlined
        class="mb-3 current-plan-chip"
      >
        Current plan
      </v-chip>
      <v-chip
        v-if="isScheduledPlan"
        color="warning"
        outlined
        small
        class="mb-3"
      >
        {{ scheduledPlanLabel }}
      </v-chip>
      <div class="display-1 font-weight-bold">
        {{ formatPlanPrice(plan) }}
        <span class="caption">{{ isFreePlan ? "forever" : "/ month" }}<template v-if="priceNote"> · {{ priceNote }}</template></span>
      </div>
      <div v-if="marketing" class="subtitle-1 mt-2">
        {{ marketing.creditsLine || plan.credits_per_month + (plan.features && plan.features.credit_unit_label ? " " + plan.features.credit_unit_label + " " + ($config.PRICING_USAGE_PERIOD || '/ mo') : " credits / mo") }}
      </div>
      <div v-else-if="isFreePlan" class="subtitle-1 mt-2">
        {{ freeCredits.amount }} one-time starter credits
        <span class="caption d-block">
          No daily reset - a one-time grant to try {{ $config.productName }}.
        </span>
      </div>
      <div v-else class="subtitle-1 mt-2">
        {{ plan.credits_per_month }} credits per month
      </div>
      <div v-if="!marketing && creditsEnabled" class="caption text--secondary mt-1">
        {{ creditsPayFor }}
      </div>
      <div
        v-if="!marketing && creditEstimate.videos > 0"
        class="
          caption
          text--secondary
          mt-1
          d-flex
          align-center
          justify-center
          flex-wrap
        "
      >
        <span>
          ≈ {{ creditEstimate.videos.toLocaleString() }}
          {{ creditEstimate.videos === 1 ? $config.UNIT_NOUN : $config.UNIT_NOUN_PLURAL }} / {{ estimatePeriod }}
        </span>
        <v-tooltip top max-width="260">
          <template v-slot:activator="{ on, attrs }">
            <v-icon x-small class="ml-1" v-bind="attrs" v-on="on">
              mdi-information-outline
            </v-icon>
          </template>
          <span>
            Rough estimate only - the per-job cost model is not finalised.
            Actual usage varies by job. Credits are usage units for
            processing time.
          </span>
        </v-tooltip>
      </div>

      <v-divider class="my-2"></v-divider>

      <div v-if="plan.description && !displayTagline" class="body-2 text-left mb-3">
        {{ plan.description }}
      </div>

      <div v-if="previousTierName && !marketing" class="tier-delta text-left mb-2">
        Everything in {{ previousTierName }}, plus:
      </div>

      <!-- App-specific marketing bullets ($config.PLAN_MARKETING): each
           card's list is complete on its own, replacing every generic row
           below. -->
      <v-list v-if="marketing" dense class="transparent">
        <v-list-item v-for="feature in marketing.features" :key="feature">
          <v-list-item-icon>
            <v-icon color="success">mdi-check</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ feature }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <v-list v-else dense class="transparent">
        <!-- ── Execution capacity ── -->
        <v-list-item v-if="plan.features">
          <v-list-item-icon>
            <v-icon color="success">mdi-check</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title
              >{{ concurrentJobs }} concurrent
              {{
                concurrentJobs > 1
                  ? $config.UNIT_NOUN_PLURAL
                  : $config.UNIT_NOUN
              }}</v-list-item-title
            >
          </v-list-item-content>
        </v-list-item>

        <v-list-item v-if="plan.features && plan.features.priority_support">
          <v-list-item-icon>
            <v-icon color="success">mdi-check</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>Priority support</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- API Access - available on all plans -->
        <v-list-item>
          <v-list-item-icon>
            <v-icon color="success">mdi-check</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>API access included</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Buy Additional Credits - open to every plan; subscribers check out
             at the better rate. -->
        <v-list-item>
          <v-list-item-icon>
            <v-icon color="success">mdi-check</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>
              Buy additional credits ({{ packRate }} / $1)
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card-text>
    <v-card-actions class="justify-center pb-4">
      <v-btn
        :color="actionButtonColor"
        :outlined="isCurrentPlan || isScheduledPlan"
        :disabled="isCurrentPlan || isScheduledPlan || checkoutUnavailable"
        @click="handlePlanSelection"
      >
        {{ actionButtonLabel }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { formatPlanPrice } from "@shared/utils/planPricing";
import { formatPlanName } from "@shared/utils/planLabels";
import { creditsPayFor, creditsEnabled } from "@shared/utils/productWording";

// Credits one typical job costs under the cost-plus rule. The
// real figure arrives on the credit-balance payload
// (`reference_video_credits`, derived from metered usage); this is only
// the fallback while it loads.
const DEFAULT_REFERENCE_VIDEO_CREDITS = 390;

// Round an estimated count DOWN to a clean "feel" number so the card never
// shows false precision and never over-promises (e.g. 27 -> 25).
function roundNiceCount(n) {
  if (!isFinite(n) || n <= 0) return 0;
  if (n >= 1000) return Math.floor(n / 100) * 100;
  if (n >= 100) return Math.floor(n / 50) * 50;
  if (n >= 10) return Math.floor(n / 5) * 5;
  return Math.floor(n);
}

export default {
  name: "SubscriptionPlanCard",
  props: {
    plan: {
      type: Object,
      required: true
    },
    // Feature slug from the referring CTA (PricingView forwards ?feature=...).
    // No plan row is feature-specific right now, so nothing is highlighted;
    // the prop stays so the existing binding keeps working.
    highlightFeature: {
      type: String,
      default: ""
    },
    currentPlan: {
      type: String,
      default: "Free"
    },
    scheduledPlan: {
      type: String,
      default: ""
    },
    scheduledPlanLabel: {
      type: String,
      default: "Scheduled Next"
    },
    tagline: {
      type: String,
      default: ""
    },
    recommended: {
      type: Boolean,
      default: false
    },
    // Apps without popularity data name the highlight honestly.
    recommendedLabel: {
      type: String,
      default: "Most Popular"
    },
    previousTierName: {
      type: String,
      default: ""
    }
  },
  computed: {
    creditsPayFor() {
      return creditsPayFor();
    },
    creditsEnabled() {
      return creditsEnabled();
    },
    // App-supplied marketing copy for this plan ($config.PLAN_MARKETING,
    // keyed by lowercase plan name). Null → the generic reference-app rows.
    marketing() {
      var map = this.$config && this.$config.PLAN_MARKETING;
      if (!map) return null;
      return map[String(this.plan.name || "").toLowerCase()] || null;
    },
    displayTagline() {
      if (this.marketing && this.marketing.tagline) return this.marketing.tagline;
      return this.tagline;
    },
    referenceVideoCredits() {
      var cb =
        this.$store && this.$store.getters
          ? this.$store.getters.creditBalance
          : null;
      var n = cb ? Number(cb.reference_video_credits) : NaN;
      return isFinite(n) && n > 0 ? n : DEFAULT_REFERENCE_VIDEO_CREDITS;
    },
    planName() {
      return formatPlanName(this.plan.name);
    },
    // Approximate "what your credits get you", derived from the plan's
    // allotment. Free stores 0 in `credits_per_month` (its grant is one-time),
    // so reading that column would estimate zero jobs - the row is simply
    // hidden for Free. See DEFAULT_REFERENCE_VIDEO_CREDITS above for the basis.
    creditEstimate() {
      var credits = this.isFreePlan
        ? 0
        : Number(this.plan && this.plan.credits_per_month) || 0;
      return {
        videos: roundNiceCount(credits / this.referenceVideoCredits)
      };
    },
    estimatePeriod() {
      return "mo";
    },
    // Packs are sold to every plan; only the checkout rate differs. Served on
    // the credit-balance payload so it can't drift from stripe_service.
    packRate() {
      var cb = this.$store.getters.creditBalance;
      var cp = cb ? cb.credit_packs : null;
      var rate = cp
        ? Number(
            this.isFreePlan
              ? cp.non_subscriber_per_dollar
              : cp.subscriber_per_dollar
          )
        : NaN;
      if (isFinite(rate) && rate > 0) return rate;
      return this.isFreePlan ? 7 : 10;
    },
    concurrentJobs() {
      if (this.plan.features && this.plan.features.concurrent_jobs) {
        return parseInt(this.plan.features.concurrent_jobs, 10);
      }
      return 1;
    },
    isProPlan() {
      return this.plan.name.toLowerCase() === "pro";
    },
    isHighlighted() {
      return this.recommended || this.isProPlan;
    },
    isCurrentPlan() {
      return this.plan.name.toLowerCase() === this.currentPlan.toLowerCase();
    },
    isScheduledPlan() {
      return (
        !!this.scheduledPlan &&
        this.plan.name.toLowerCase() === this.scheduledPlan.toLowerCase()
      );
    },
    // Opt-in app text after paid prices, such as "incl. VAT"; none by default.
    priceNote() {
      return (!this.isFreePlan && this.$config && this.$config.PRICE_NOTE) || "";
    },
    isFreePlan() {
      return (
        this.plan.name.toLowerCase() === "free" ||
        this.plan.id === 1 ||
        this.plan.price === 0 ||
        this.plan.price_monthly === 0
      );
    },
    freeCredits() {
      return this.$store.getters.freeCredits;
    },
    actionButtonColor() {
      if (this.isCurrentPlan || this.isScheduledPlan) {
        return "grey";
      }

      return "primary";
    },
    actionButtonLabel() {
      if (this.isCurrentPlan) {
        return "Current Plan";
      }

      if (this.isScheduledPlan) {
        return this.scheduledPlanLabel;
      }

      return this.checkoutUnavailable ? "Available soon" : "Select Plan";
    },
    checkoutUnavailable() {return this.plan.checkout_available === false;}
  },
  methods: {
    formatPlanPrice(plan) { return formatPlanPrice(plan, this.$config); },
    handlePlanSelection() {
      if(this.checkoutUnavailable)return;
      console.log("Plan selected:", this.plan.name, "Plan ID:", this.plan.id);
      console.log("Plan details:", JSON.stringify(this.plan));

      // Check if this is the free plan (case-insensitive or by ID)
      const isFree =
        this.plan.name.toLowerCase() === "free" ||
        this.plan.id === 1 || // Assuming free plan has ID 1
        this.plan.price === 0 ||
        this.plan.price_monthly === 0;

      console.log("Is free plan?", isFree);

      if (isFree) {
        console.log("Free plan detected, showing confirmation");
        if (
          confirm(
            "Are you sure you want to downgrade to the Free plan? This will cancel your current subscription."
          )
        ) {
          this.$emit("free-plan-selected");
        }
      } else {
        // For paid plans, emit the regular event
        this.$emit("select-plan", this.plan);
      }
    }
  }
};
</script>

<style scoped>
.subscription-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.subscription-card .v-card__text {
  flex-grow: 1;
}

.subscription-card .v-card__actions {
  margin-top: auto;
}

.subscription-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 5px 15px var(--shadow-xs);
}

/* Neutral: the accent outline belongs to the recommended card alone (a current
   plan that is also the recommended one keeps the accent, set below). */
.current-plan {
  border: 2px solid var(--border-input) !important;
}

.current-plan-chip {
  font-weight: 600;
}

.popular-plan {
  border: 2px solid var(--color-info-dark) !important;
  transform: scale(1.02);
}

.popular-plan:hover {
  transform: translateY(-5px) scale(1.02);
}

.popular-badge {
  position: absolute;
  top: 10px;
  right: -30px;
  background-color: var(--color-info-dark);
  color: var(--text-primary);
  padding: 5px 30px;
  transform: rotate(45deg);
  font-size: 0.8rem;
  font-weight: bold;
  box-shadow: 0 2px 4px var(--shadow-md);
}

.recommended-band {
  background-color: var(--color-info-dark);
  color: var(--text-on-brand, #ffffff);
  text-align: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 6px 8px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.plan-tagline {
  text-align: center;
  color: var(--text-secondary);
  font-size: 0.85rem;
  margin-top: -4px;
  margin-bottom: 4px;
  padding: 0 12px;
}

.tier-delta {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-blue-light, #3b82f6);
}

/* Denser layout so the Select Plan CTA stays high on the page */
.subscription-card ::v-deep .v-card__title {
  padding-top: 12px;
  padding-bottom: 2px;
}

.subscription-card ::v-deep .v-card__text {
  padding-top: 8px;
}

.subscription-card ::v-deep .v-list-item {
  min-height: 30px;
}

.subscription-card ::v-deep .v-list-item__icon {
  margin-top: 4px;
  margin-bottom: 4px;
  margin-right: 12px !important;
  align-self: flex-start;
}

/* Allow feature text to wrap instead of truncating */
.subscription-card ::v-deep .v-list-item__title {
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
}
</style>
